import { useQuery } from '@tanstack/react-query';
import { format, parseISO, differenceInDays } from 'date-fns';
import { api } from '../api/client';
import { useAuth } from '../stores/auth';
import { formatUzs } from '../lib/format';
import { toast } from '../stores/toasts';

interface PlanInfo {
  code: string;
  name: string;
  monthlyPrice: string | number;
  currency: string;
  limits?: {
    ordersPerMonth?: number;
    users?: number;
    storageGB?: number;
  } | null;
  isActive: boolean;
}

interface SubscriptionData {
  hasWorkshop: boolean;
  workshop?: { id: string; name: string; plan: string } | null;
  subscription?: {
    id: string;
    plan: string;
    status: string;
    startAt: string;
    endAt: string;
    daysRemaining: number;
    amount: number;
    currency: string;
    autoRenew: boolean;
  } | null;
  plans: PlanInfo[];
}

const PLAN_COLORS: Record<string, string> = {
  FREE: 'var(--text-dim)',
  STARTER: '#3b82f6',
  PRO: '#8b5cf6',
};

const STATUS_LABELS: Record<string, string> = {
  TRIAL: '🆓 Sinov muddati',
  ACTIVE: '✅ Faol',
  EXPIRED: '⛔️ Tugagan',
  CANCELLED: '❌ Bekor qilingan',
};

const PLAN_LABELS: Record<string, string> = {
  FREE: 'Bepul',
  STARTER: 'Boshlovchi',
  PRO: 'Pro',
};

export function SubscriptionPage() {
  const auth = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ['my-subscription'],
    queryFn: () => api.mySubscription(),
  });

  const tg = (window as any).Telegram?.WebApp;

  const sub = data as SubscriptionData | undefined;

  if (isLoading) return <div className="spinner" />;

  if (!sub?.hasWorkshop) {
    return (
      <>
        <h3>💳 Tarif va obuna</h3>
        <div className="empty">
          <div className="emoji">🏢</div>
          <div>Usta oching</div>
          <div className="muted" style={{ marginTop: 4 }}>
            Tarif rasmiylashtirish uchun avval Mini App orqali usta oching.
          </div>
        </div>
        {sub?.plans && sub.plans.length > 0 && (
          <>
            <h3 style={{ marginTop: 12 }}>📋 Mavjud tariflar</h3>
            {sub.plans.map((p) => (
              <PlanCard key={p.code} plan={p} current={false} />
            ))}
          </>
        )}
      </>
    );
  }

  const workshop = sub.workshop!;
  const current = sub.subscription;
  const planLabel = PLAN_LABELS[workshop.plan] ?? workshop.plan;
  const daysTotal = current ? differenceInDays(parseISO(current.endAt), new Date()) : 0;
  const urgent = current && current.status !== 'EXPIRED' && current.daysRemaining <= 7;

  function contactAdmin() {
    if (tg?.openTelegramLink) {
      const handle = (import.meta.env.VITE_ADMIN_CONTACT as string) || '@autoservice_admin';
      tg.openTelegramLink(`https://t.me/${handle.replace(/^@/, '')}`);
    } else {
      toast.info('Mini App orqali oching');
    }
  }

  return (
    <>
      <h3>💳 Tarif va obuna</h3>

      <div className="card" style={{ background: 'linear-gradient(135deg, var(--surface), var(--surface-2))' }}>
        <div className="row-between" style={{ marginBottom: 6 }}>
          <div style={{ fontWeight: 600, fontSize: 16 }}>🏢 {workshop.name}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
          <span
            className="tag"
            style={{
              background: PLAN_COLORS[workshop.plan] ?? 'var(--primary)',
              color: '#fff',
              fontSize: 13,
              padding: '4px 10px',
            }}
          >
            {planLabel}
          </span>
          {current && (
            <span className="tag">{STATUS_LABELS[current.status] ?? current.status}</span>
          )}
        </div>
        {current && (
          <>
            <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>
              📅 {format(parseISO(current.startAt), 'dd MMM yyyy')} — {format(parseISO(current.endAt), 'dd MMM yyyy')}
            </div>
            {current.status !== 'EXPIRED' && (
              <div className="row-between" style={{ marginTop: 10 }}>
                <span className="muted" style={{ fontSize: 12 }}>Qolgan kunlar</span>
                <span style={{ fontWeight: 700, fontSize: 18, color: urgent ? '#f59e0b' : 'var(--primary)' }}>
                  {current.daysRemaining} kun
                </span>
              </div>
            )}
            {urgent && current.status !== 'EXPIRED' && (
              <div
                className="card"
                style={{
                  background: 'rgba(245, 158, 11, 0.1)',
                  border: '1px solid #f59e0b',
                  marginTop: 10,
                  padding: 10,
                }}
              >
                <div style={{ fontSize: 13 }}>
                  ⚠️ Tarif tugashiga {current.daysRemaining} kun qoldi. To'lov qilish uchun admin bilan bog'laning.
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <button className="btn btn-primary btn-block" onClick={contactAdmin} style={{ marginTop: 10 }}>
        💬 Admin bilan bog'lanish
      </button>

      <h3 style={{ marginTop: 16 }}>📋 Mavjud tariflar</h3>
      {sub.plans.length === 0 ? (
        <div className="empty">Hozircha tariflar yo'q</div>
      ) : (
        sub.plans.map((p) => (
          <PlanCard key={p.code} plan={p} current={workshop.plan === p.code} />
        ))
      )}

      <div className="muted" style={{ fontSize: 11, marginTop: 14, textAlign: 'center' }}>
        To'lov qabul qilish vaqtincha qo'lda amalga oshiriladi.
        <br />
        Faollashtirish uchun admin bilan bog'laning.
      </div>
    </>
  );
}

function PlanCard({ plan, current }: { plan: PlanInfo; current: boolean }) {
  const color = PLAN_COLORS[plan.code] ?? 'var(--primary)';
  const price = Number(plan.monthlyPrice);
  const label = PLAN_LABELS[plan.code] ?? plan.name;
  const limits: string[] = [];
  if (plan.limits?.ordersPerMonth) limits.push(`${plan.limits.ordersPerMonth} buyurtma/oy`);
  if (plan.limits?.users) limits.push(`${plan.limits.users} xodim`);
  if (plan.limits?.storageGB) limits.push(`${plan.limits.storageGB} GB`);
  return (
    <div
      className="card"
      style={{
        border: current ? `2px solid ${color}` : undefined,
        position: 'relative',
      }}
    >
      {current && (
        <span
          className="tag"
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            background: color,
            color: '#fff',
          }}
        >
          Hozirgi
        </span>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            background: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontWeight: 700,
          }}
        >
          {label.charAt(0)}
        </div>
        <div>
          <div style={{ fontWeight: 600, fontSize: 16 }}>{label}</div>
          <div className="muted" style={{ fontSize: 12 }}>{plan.name}</div>
        </div>
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, margin: '8px 0' }}>
        {price > 0 ? formatUzs(price) : 'Bepul'}
        <span className="muted" style={{ fontSize: 12, fontWeight: 400 }}> /oy</span>
      </div>
      {limits.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {limits.map((l, i) => (
            <div key={i} style={{ fontSize: 12 }}>✅ {l}</div>
          ))}
        </div>
      )}
    </div>
  );
}
